import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { SortIcon } from '@/components/common/SortIcon';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { formatCurrency } from '@/lib/utils/currency';
import { PaymentStatusBadge } from './PaymentStatusBadge';
import { PaymentActionsCell } from './PaymentActionsCell';
import type { Payment, PaymentFilters } from '../types';

interface GetColumnsParams {
  orderBy: string;
  order: 'asc' | 'desc' | '';
  onSort: (field: string) => void;
  filters: PaymentFilters;
}

export function usePaymentColumns({
  orderBy,
  order,
  onSort,
}: GetColumnsParams): ColumnDef<Payment>[] {
  const { t } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);

  const columns: ColumnDef<Payment>[] = [
    {
      accessorKey: 'description',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('description')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('description')}
        >
          <p>{t('description')}</p>
          <SortIcon active={orderBy === 'description'} direction={order} />
        </div>
      ),
      cell: ({ row }) => (
        <Link
          to={`/app/payments/${row.original.id}`}
          className="text-blue-700 hover:underline"
        >
          {row.original.description || `Payment #${row.original.id}`}
        </Link>
      ),
    },
    {
      accessorKey: 'amount',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('amount')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('amount')}
        >
          <p>{t('amount')}</p>
          <SortIcon active={orderBy === 'amount'} direction={order} />
        </div>
      ),
      cell: ({ row }) => (
        <div>
          {formatCurrency(row.original.amount, {
            locale: 'uz-UZ',
            currency: row.original.currency,
          })}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('status')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('status')}
        >
          <p>{t('status.status')}</p>
          <SortIcon active={orderBy === 'status'} direction={order} />
        </div>
      ),
      cell: ({ row }) => <PaymentStatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'paidAt',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('paidAt')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('paidAt')}
        >
          <p>{t('paidAt')}</p>
          <SortIcon active={orderBy === 'paidAt'} direction={order} />
        </div>
      ),
      cell: ({ row }) => {
        const paidAt = row.original.paidAt;
        return paidAt ? <div>{format(new Date(paidAt), 'MM/dd/yyyy HH:mm')}</div> : <span>-</span>;
      },
    },
    {
      accessorKey: 'client',
      header: () => <p>{t('client')}</p>,
      cell: ({ row }) => {
        const client = row.original.client;
        if (!client?.id) return <span className="text-muted-foreground">-</span>;
        return (
          <Link to={`/app/clients/${client.id}`} className="text-blue-700 hover:underline">
            {client.name || t('unnamed')}
          </Link>
        );
      },
    },
    {
      accessorKey: 'paymentMethod',
      header: () => <p>{t('paymentMethod')}</p>,
      cell: ({ row }) => {
        const pm = row.original.paymentMethod;
        if (!pm?.id) return <span className="text-muted-foreground">-</span>;
        return (
          <Link to={`/app/payment-methods/${pm.id}`} className="text-blue-700 hover:underline">
            {pm.name || t('unnamed')}
          </Link>
        );
      },
    },
    {
      accessorKey: 'contract',
      header: () => <p>{t('contract')}</p>,
      cell: ({ row }) => {
        const contract = row.original.contract;
        if (!contract?.id) return <span className="text-muted-foreground">-</span>;
        return (
          <Link
            to={`/app/contracts/${contract.id}`}
            className="text-blue-700 hover:underline"
          >
            {contract.name || `#${contract.id}`}
          </Link>
        );
      },
    },
    {
      accessorKey: 'bill',
      header: () => <p>{t('bill')}</p>,
      cell: ({ row }) => {
        const bill = row.original.bill;
        if (!bill?.id)
          return (
            <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
              {t('unallocated')}
            </span>
          );
        return (
          <Link to={`/app/bills/${bill.id}`} className="text-blue-700 hover:underline">
            #{String(bill.id).padStart(5, '0')}
          </Link>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('createdAt')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('createdAt')}
        >
          <p>{t('createdAt')}</p>
          <SortIcon active={orderBy === 'createdAt'} direction={order} />
        </div>
      ),
      cell: ({ row }) => {
        const createdAt = row.original.createdAt;
        return createdAt ? (
          <div>{format(new Date(createdAt), 'MM/dd/yyyy HH:mm')}</div>
        ) : (
          <span>-</span>
        );
      },
    },
  ];

  // Add actions column if user has permissions
  if (hasAccess('payment', 'update') || hasAccess('payment', 'delete')) {
    columns.splice(0, 0, {
      id: 'actions',
      header: t('actionsLabel'),
      cell: ({ row }) => <PaymentActionsCell payment={row.original} />,
    });
  }

  return columns;
}
